import { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";

interface Habit {
  id: number;
  name: string;
  goal_per_week: number;
  archived: number;
}

interface Completion {
  id: number;
  habit_id: number;
  completed_date: string;
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

export function ReportsPage({ onBack }: { onBack: () => void }) {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [loading, setLoading] = useState(true);
  const [heatmapDate, setHeatmapDate] = useState(new Date());
  const [weekDate, setWeekDate] = useState(new Date());
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    fetch("/api/reports")
      .then((r) => r.json())
      .then((data: { habits: Habit[]; completions: Completion[] }) => {
        setHabits(data.habits);
        setCompletions(data.completions);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const completionsByDate = useMemo(() => {
    const map = new Map<string, Completion[]>();
    for (const c of completions) {
      const existing = map.get(c.completed_date);
      if (existing) existing.push(c);
      else map.set(c.completed_date, [c]);
    }
    return map;
  }, [completions]);

  const completionsByHabit = useMemo(() => {
    const map = new Map<number, Completion[]>();
    for (const c of completions) {
      const existing = map.get(c.habit_id);
      if (existing) existing.push(c);
      else map.set(c.habit_id, [c]);
    }
    return map;
  }, [completions]);

  // --- Weekly Goal Tracker ---
  const activeHabits = habits.filter((h) => !h.archived);
  const monday = getMonday(weekDate);
  const weekDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    weekDays.push(formatDate(d));
  }
  const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];

  const currentWeekMonday = getMonday(new Date());
  const isCurrentWeek = formatDate(monday) === formatDate(currentWeekMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const weekLabel = isCurrentWeek ? "This Week" : `${fmt(monday)} – ${fmt(sunday)}`;

  function shiftWeek(deltaDays: number) {
    const d = new Date(weekDate);
    d.setDate(d.getDate() + deltaDays);
    setWeekDate(d);
  }

  function handleWeekTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }

  function handleWeekTouchEnd(e: React.TouchEvent) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    shiftWeek(dx < 0 ? 7 : -7);
  }

  // --- Heatmap ---
  const heatmapYear = heatmapDate.getFullYear();
  const heatmapMonth = heatmapDate.getMonth();
  const firstDay = new Date(heatmapYear, heatmapMonth, 1);
  const lastDay = new Date(heatmapYear, heatmapMonth + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const heatmapWeeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = Array(startPad).fill(null) as null[];
  for (let day = 1; day <= totalDays; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      heatmapWeeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    heatmapWeeks.push(currentWeek);
  }

  const heatmapMonthLabel = heatmapDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  function getHeatmapColor(day: number): string {
    const dateStr = `${heatmapYear}-${String(heatmapMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayCompletions = completionsByDate.get(dateStr);
    const count = dayCompletions ? dayCompletions.length : 0;
    if (count === 0) return "bg-white/5";
    if (count <= 2) return "bg-emerald-500/30";
    return "bg-emerald-500";
  }

  function getHeatmapCount(day: number): number {
    const dateStr = `${heatmapYear}-${String(heatmapMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayCompletions = completionsByDate.get(dateStr);
    return dayCompletions ? dayCompletions.length : 0;
  }

  // --- Stats ---
  const stats = useMemo(() => {
    if (completions.length === 0) {
      return { currentStreak: 0, bestStreak: 0, totalCompletions: 0, dayOfWeek: new Array(7).fill(0) as number[] };
    }

    const totalCompletions = completions.length;

    // Unique dates with at least one completion
    const uniqueDates = [...new Set(completions.map((c) => c.completed_date))].sort();

    // Current streak: consecutive days ending at today (or most recent)
    const today = formatDate(new Date());
    let currentStreak = 0;
    const dateSet = new Set(uniqueDates);
    let checkDate = new Date();

    // If today has no completions, start from yesterday
    if (!dateSet.has(today)) {
      checkDate.setDate(checkDate.getDate() - 1);
    }

    while (dateSet.has(formatDate(checkDate))) {
      currentStreak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }

    // Best streak
    let bestStreak = 0;
    let streak = 1;
    for (let i = 1; i < uniqueDates.length; i++) {
      const prev = new Date(uniqueDates[i - 1] + "T00:00:00");
      const curr = new Date(uniqueDates[i] + "T00:00:00");
      const diffDays = Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        streak++;
      } else {
        bestStreak = Math.max(bestStreak, streak);
        streak = 1;
      }
    }
    bestStreak = Math.max(bestStreak, streak);

    // Day-of-week breakdown (0=Mon, 6=Sun)
    const dayOfWeek = new Array(7).fill(0) as number[];
    for (const c of completions) {
      const d = new Date(c.completed_date + "T00:00:00");
      const dow = (d.getDay() + 6) % 7; // Convert to Mon=0
      dayOfWeek[dow]++;
    }

    return { currentStreak, bestStreak, totalCompletions, dayOfWeek };
  }, [completions]);

  const maxDow = Math.max(...stats.dayOfWeek, 1);

  // Per-habit stats
  const habitStats = useMemo(() => {
    return habits.map((h) => {
      const hc = completionsByHabit.get(h.id) || [];
      const total = hc.length;
      // Weekly average: total / weeks since first completion (min 1 week)
      let weeklyAvg = 0;
      let firstDate: string | null = null;
      if (hc.length > 0) {
        const sortedDates = hc.map((c) => c.completed_date).sort();
        firstDate = sortedDates[0];
        const first = new Date(firstDate + "T00:00:00");
        const now = new Date();
        const weeks = Math.max(1, Math.ceil((now.getTime() - first.getTime()) / (1000 * 60 * 60 * 24 * 7)));
        weeklyAvg = total / weeks;
      }
      return { habit: h, total, weeklyAvg, firstDate };
    });
  }, [habits, completionsByHabit]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-white/40 text-sm">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4 py-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-white/40 hover:text-white/70 transition-colors mb-6 cursor-pointer"
      >
        <ArrowLeft size={16} />
        Back to habits
      </button>

      {/* Weekly Goal Tracker */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
          Weekly Progress
        </h2>
        <div
          className="bg-[#16213e] border border-white/10 rounded-xl p-3 touch-pan-y select-none"
          onTouchStart={handleWeekTouchStart}
          onTouchEnd={handleWeekTouchEnd}
        >
          {/* Week navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => shiftWeek(-7)}
              className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
              aria-label="Previous week"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-medium text-white">{weekLabel}</span>
            <button
              onClick={() => shiftWeek(7)}
              className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
              aria-label="Next week"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: "minmax(0, 1fr) repeat(7, 24px) auto" }}>
            <div />
            {dayLabels.map((label, i) => (
              <div key={i} className="text-center text-[10px] text-white/30 font-medium">
                {label}
              </div>
            ))}
            <div />
          </div>

          {activeHabits.length === 0 && (
            <p className="text-sm text-white/20 text-center py-4">No active habits</p>
          )}

          {activeHabits.map((habit) => {
            const habitCompletions = new Set(
              (completionsByHabit.get(habit.id) || []).map((c) => c.completed_date)
            );
            const weekCount = weekDays.filter((d) => habitCompletions.has(d)).length;
            const goalMet = weekCount >= habit.goal_per_week;

            return (
              <div
                key={habit.id}
                className="grid gap-1 items-center mb-1.5"
                style={{ gridTemplateColumns: "minmax(0, 1fr) repeat(7, 24px) auto" }}
              >
                <div className="text-xs text-white truncate pr-1">{habit.name}</div>
                {weekDays.map((day, i) => {
                  const done = habitCompletions.has(day);
                  return (
                    <div key={i} className="flex items-center justify-center">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${
                          done
                            ? "bg-emerald-500 text-white"
                            : "bg-white/5 text-white/20"
                        }`}
                      >
                        {done ? "\u2713" : ""}
                      </div>
                    </div>
                  );
                })}
                <div
                  className={`text-xs font-medium text-right whitespace-nowrap pl-2 ${
                    goalMet ? "text-emerald-400" : "text-white/40"
                  }`}
                >
                  {weekCount}/{habit.goal_per_week}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Monthly Heatmap */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
          Monthly Heatmap
        </h2>
        <div className="bg-[#16213e] border border-white/10 rounded-xl p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                const d = new Date(heatmapDate);
                d.setMonth(d.getMonth() - 1);
                setHeatmapDate(d);
              }}
              className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-medium text-white">{heatmapMonthLabel}</span>
            <button
              onClick={() => {
                const d = new Date(heatmapDate);
                d.setMonth(d.getMonth() + 1);
                setHeatmapDate(d);
              }}
              className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-2 mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <div key={d} className="text-center text-[10px] text-white/30 font-medium py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Grid */}
          {heatmapWeeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-2">
              {week.map((day, di) => {
                if (day === null) {
                  return <div key={di} className="aspect-square" />;
                }
                const count = getHeatmapCount(day);
                const colorClass = getHeatmapColor(day);
                const isToday =
                  `${heatmapYear}-${String(heatmapMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` ===
                  formatDate(new Date());

                return (
                  <div
                    key={di}
                    className={`aspect-square flex items-center justify-center rounded-md text-xs transition-colors ${colorClass} ${
                      isToday ? "ring-1 ring-white/30" : ""
                    }`}
                    title={`${count} completion${count !== 1 ? "s" : ""}`}
                  >
                    <span className="text-white/60">{day}</span>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center gap-3 mt-4 text-[10px] text-white/40">
            <span>Less</span>
            <div className="w-4 h-4 rounded bg-white/5" />
            <div className="w-4 h-4 rounded bg-emerald-500/30" />
            <div className="w-4 h-4 rounded bg-emerald-500" />
            <span>More</span>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
          Stats
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#16213e] border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{stats.currentStreak}</div>
            <div className="text-[10px] text-white/40 mt-1">Current Streak</div>
          </div>
          <div className="bg-[#16213e] border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{stats.bestStreak}</div>
            <div className="text-[10px] text-white/40 mt-1">Best Streak</div>
          </div>
          <div className="bg-[#16213e] border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{stats.totalCompletions}</div>
            <div className="text-[10px] text-white/40 mt-1">Total</div>
          </div>
        </div>

        {/* Day-of-week breakdown */}
        <div className="bg-[#16213e] border border-white/10 rounded-xl p-4 mb-4">
          <h3 className="text-xs font-medium text-white/50 mb-3">Day of Week</h3>
          <div className="space-y-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-white/40 w-8">{label}</span>
                <div className="flex-1 h-4 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/60 rounded-full transition-all"
                    style={{ width: `${(stats.dayOfWeek[i] / maxDow) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-white/30 w-8 text-right">{stats.dayOfWeek[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Per-habit stats */}
        <div className="bg-[#16213e] border border-white/10 rounded-xl p-4">
          <h3 className="text-xs font-medium text-white/50 mb-3">Per Habit</h3>
          <div className="space-y-2">
            {habitStats.map(({ habit, total, weeklyAvg, firstDate }) => (
              <div key={habit.id} className="flex items-center justify-between">
                <span className={`text-sm truncate flex-1 ${habit.archived ? "text-white/30" : "text-white/70"}`}>
                  {habit.name}
                  {habit.archived ? " (archived)" : ""}
                </span>
                <div className="flex items-center gap-4 text-xs text-white/40 flex-shrink-0">
                  <span>{total} total</span>
                  <span>{weeklyAvg.toFixed(1)}/wk</span>
                  {firstDate && <span>since {`${firstDate.slice(5,7)}/${firstDate.slice(8,10)}/${firstDate.slice(2,4)}`}</span>}
                </div>
              </div>
            ))}
            {habitStats.length === 0 && (
              <p className="text-sm text-white/20 text-center">No habits yet</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
